import type { ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth } from '@iplan/auth/react';
import {
  InsetSidebar,
  InsetSidebarLayout,
  InsetSidebarProvider,
  InsetSidebarTrigger,
  type SidebarNavCategoryGroup,
  type SidebarNavSection,
} from '@iplan/ui/sidebar';
import { Separator } from '@iplan/ui/primitives/separator';
import {
  BarChart3,
  Boxes,
  Calculator,
  CalendarDays,
  ChevronRight,
  ClipboardList,
  Clock,
  Coins,
  FileInput,
  FileSpreadsheet,
  FileText,
  Hammer,
  Home,
  Info,
  Settings,
  Truck,
  UserCog,
  type LucideIcon,
} from 'lucide-react';
import {
  angularBaseUrl,
  authLogoutUri,
  authMyAccountUri,
  oldStackUrl,
  timesheetUrl,
} from '@/lib/config';
import { findAdminTab } from '@/layouts/adminTabs';
import { GlobalSearch } from '@/components/GlobalSearch';
import { ThemeToggle } from '@/components/ThemeToggle';
import { EventSelector } from '@/components/EventSelector';
import { EventProvider, useEventContext, isEventScopedRoute } from '@/contexts/EventContext';
import { BreadcrumbProvider, useBreadcrumbContext, type Crumb } from '@/contexts/BreadcrumbContext';
import { PageTitleProvider, usePageTitle, usePageTitleContext } from '@/contexts/PageTitleContext';

/**
 * An Old Stack (AngularJS) external-link nav section. These surfaces are not yet
 * replatformed, so they deep-link back into the legacy host. They are folded
 * into the natural nav groups below (rather than a separate "Legacy" group) so
 * they read as first-class destinations; they only resolve when the Angular
 * host is configured, so {@link filterNavGroups} strips every `external` section
 * when `VITE_ANGULAR_BASE_URL` is unset (bar the {@link ALWAYS_EXTERNAL_KEYS}
 * exceptions). `oldStackUrl` builds the bare path in that case.
 */
function oldStackSection(
  key: string,
  label: string,
  icon: LucideIcon,
  path: string,
): SidebarNavSection {
  return { key, label, icon, external: true, externalUrl: oldStackUrl(path) };
}

/**
 * Sidebar navigation, grouped by domain. New-stack pages (`href`) and not-yet-
 * replatformed Old Stack links ({@link oldStackSection}) sit side by side within
 * each group — the migration boundary is intentionally invisible to the user.
 * Every static Old Stack surface is placed in the group its function belongs to.
 *
 * "Home" is the Old Stack dashboard (`/#/`): the new app's card-grid home screen
 * has been retired, so Home always points at Angular with no new-stack fallback
 * (see {@link ALWAYS_EXTERNAL_KEYS}).
 */
export const NAV_GROUPS: SidebarNavCategoryGroup[] = [
  {
    label: 'Overview',
    sections: [
      oldStackSection('home', 'Home', Home, '/#/'),
      oldStackSection('legacy-reports', 'Reports', BarChart3, '/#/reports'),
    ],
  },
  {
    label: 'Planning',
    sections: [
      oldStackSection('legacy-events', 'Events', CalendarDays, '/#/events'),
      { key: 'orders', label: 'Orders', icon: ClipboardList, href: '/orders' },
      oldStackSection('legacy-workpacks', 'Work Packs', Boxes, '/#/workpacks'),
    ],
  },
  {
    label: 'Costs',
    sections: [
      { key: 'valuations', label: 'Valuations', icon: Calculator, href: '/valuations' },
      {
        key: 'settlement-sheets',
        label: 'Settlement Sheets',
        icon: FileSpreadsheet,
        href: '/settlement-sheets',
      },
      { key: 'rates', label: 'Labour Rate Sets', icon: Coins, href: '/rates' },
    ],
  },
  {
    label: 'Resources',
    sections: [
      // Not an oldStackSection: the Timesheet link's target isn't always the
      // Angular host. It matches the Old Stack's own TimesheetsExternalBaseUrl
      // behaviour (Web/Views/Shared/_Layout.cshtml) — an external timesheets
      // system when the pipeline configures one, falling back to the internal
      // Angular route (what oldStackSection would have built) otherwise.
      {
        key: 'legacy-timesheet',
        label: 'Timesheet',
        icon: Clock,
        external: true,
        externalUrl: timesheetUrl(),
      },
      oldStackSection('legacy-scaffold', 'Scaffold', Hammer, '/#/scaffold'),
      oldStackSection('legacy-equipment', 'Equipment', Truck, '/#/equipment'),
    ],
  },
  {
    label: 'Admin',
    sections: [
      { key: 'admin', label: 'Admin', icon: Settings, href: '/admin' },
      oldStackSection('legacy-administration', 'Administration', UserCog, '/#/administration'),
      oldStackSection('legacy-importexport', 'Import/Export', FileInput, '/#/importexport'),
      oldStackSection('legacy-info', 'Info', Info, '/#/info'),
    ],
  },
];

/**
 * External nav sections that survive the "no Angular host configured" strip in
 * {@link filterNavGroups}. Home has no new-stack equivalent any more, so it is
 * always rendered as an Angular link — it simply resolves to a bare `/#/` (and
 * is broken) in a build with no `VITE_ANGULAR_BASE_URL`. Accepted trade-off:
 * a visible-but-broken Home beats a sidebar with no Home at all.
 */
const ALWAYS_EXTERNAL_KEYS = new Set(['home']);

/**
 * Old Stack links are baked into NAV_GROUPS but only resolve when the Angular
 * host is configured. When it isn't, drop every external section (and any group
 * left empty) so the new-stack-only build shows just its own pages — except the
 * {@link ALWAYS_EXTERNAL_KEYS} entries, which are kept regardless.
 */
export function filterNavGroups(
  groups: SidebarNavCategoryGroup[],
  hasAngularHost: boolean,
): SidebarNavCategoryGroup[] {
  if (hasAngularHost) return groups;
  return groups
    .map((group) => ({
      ...group,
      sections: group.sections.filter(
        (section) => !section.external || ALWAYS_EXTERNAL_KEYS.has(section.key),
      ),
    }))
    .filter((group) => group.sections.length > 0);
}

/**
 * Rewrites a legacy event-scoped URL to the canonical new-stack path that
 * serves it, for the purposes of nav matching only.
 *
 * The Old Stack (and the previous React app) deep-link orders as
 * `/events/:eventId/orders[/:orderId]`; `router.tsx` maps those onto the same
 * `OrdersPage` as `/orders`, with the event id driving `EventContext`. The nav
 * registry has no `/events` entry, though, so without this the breadcrumb trail
 * comes back empty, the topbar falls back to a generic label, and the sidebar's
 * "Orders" item never highlights on those URLs.
 *
 * A bare `/events/:eventId` collapses to `/` rather than `''`, so callers never
 * have to special-case an empty pathname.
 */
export function canonicalNavPath(pathname: string): string {
  const match = /^\/events\/[^/]+(\/.*)?$/.exec(pathname);
  if (!match) return pathname;
  return match[1] ?? '/';
}

/**
 * The nav/admin registry entry a pathname belongs to — the label and Lucide
 * icon shown by the topbar {@link PageTitle}.
 *
 * Admin routes resolve against the admin tab registry first (the single
 * `/admin` nav entry prefix-matches every `/admin/*` path); otherwise the
 * longest-prefix nav section wins, so nested routes (e.g. `/orders/:id/blinds`)
 * inherit "Orders" and its icon. Returns `undefined` for paths in neither
 * registry — the caller supplies the fallback icon.
 */
export function resolvePageIdentity(
  rawPathname: string,
): { label: string; icon: LucideIcon } | undefined {
  const pathname = canonicalNavPath(rawPathname);
  const adminMatch = findAdminTab(pathname);
  if (adminMatch) return { label: adminMatch.tab.label, icon: adminMatch.tab.icon };

  let best: { href: string; label: string; icon: LucideIcon } | undefined;
  for (const group of NAV_GROUPS) {
    for (const section of group.sections) {
      const href = section.href;
      if (!href) continue;
      if (pathname !== href && !pathname.startsWith(`${href}/`)) continue;
      if (!best || href.length > best.href.length) {
        best = { href, label: section.label, icon: section.icon };
      }
    }
  }
  return best ? { label: best.label, icon: best.icon } : undefined;
}

/**
 * Derives a breadcrumb trail from the current pathname by matching it against
 * the NAV_GROUPS sections (mirrors iPlan4's header breadcrumb). The matched
 * section contributes its group label + page label; the longest-prefix match
 * wins so nested routes (e.g. /orders/:id/revisions) still resolve to "Orders".
 *
 * Admin routes resolve against the admin tab registry instead — the single
 * `/admin` nav entry prefix-matches every `/admin/*` path, so the nav
 * structure alone could only ever yield "Admin › Admin".
 */
export function buildBreadcrumbs(rawPathname: string): Crumb[] {
  const pathname = canonicalNavPath(rawPathname);
  const adminMatch = findAdminTab(pathname);
  if (adminMatch) {
    return [{ label: 'Admin' }, { label: adminMatch.tab.label, href: adminMatch.tab.href }];
  }

  let best: { groupLabel: string; href: string; label: string } | undefined;

  for (const group of NAV_GROUPS) {
    for (const section of group.sections) {
      const href = section.href;
      if (!href) continue;
      const matches = pathname === href || pathname.startsWith(`${href}/`);
      if (!matches) continue;
      if (!best || href.length > best.href.length) {
        best = { groupLabel: group.label, href, label: section.label };
      }
    }
  }

  if (!best) return [];
  return [{ label: best.groupLabel }, { label: best.label, href: best.href }];
}

/**
 * Top-bar page title — icon in a tinted box + the page's title, mirroring
 * iPlan4's header. This replaces the per-page `<h1>` that `PageHeader` used to
 * render, reclaiming a full title block of vertical space above every grid.
 *
 * The title is whatever the page published via `PageHeader` (see
 * `PageTitleContext`); pages that mount no `PageHeader` fall back to their
 * nav/admin registry label. The icon always comes from the registry — a page
 * title is free text, but its icon should stay consistent with the sidebar.
 */
function PageTitle(): ReactNode {
  const location = useLocation();
  const { published } = usePageTitleContext();

  const identity = resolvePageIdentity(location.pathname);
  // A title published for a different pathname is stale (the previous page is
  // unmounting, or its replacement hasn't run its publish effect yet).
  const title = published?.pathname === location.pathname ? published.title : identity?.label;
  if (!title) return null;

  const Icon = identity?.icon ?? FileText;
  return (
    <div className="flex min-w-0 items-center gap-2" data-testid="topbar-page-title">
      <div className="shrink-0 rounded-md bg-iplan-brand/10 p-1.5">
        <Icon className="size-4 text-iplan-brand" aria-hidden />
      </div>
      <h1 className="truncate text-sm font-semibold text-foreground">{title}</h1>
    </div>
  );
}

/**
 * Top-bar breadcrumb trail, with the vertical rule that separates it from the
 * page title. Renders nothing (rule included) when no nav section matches.
 *
 * The trail is the first thing to go on narrow screens (`hidden sm:flex`): its
 * last crumb repeats the page title that now sits immediately to its left, so
 * it is the most redundant item in the topbar — the title, event selector and
 * search all carry information it doesn't.
 *
 * A page can publish its own trail via `usePageBreadcrumbs` (BreadcrumbContext),
 * which takes precedence — used where the trail can't be derived from the path
 * (e.g. the per-order Day-Work Labour surface, which resolves the order number
 * from an id). Otherwise the trail is derived from the nav structure, and on
 * event-scoped routes (Orders / Operations) the currently selected event's name
 * is appended as the final crumb so the user always sees which event they are
 * working within.
 */
function Breadcrumbs(): ReactNode {
  const location = useLocation();
  const { selectedEvent } = useEventContext();
  const { crumbs: pageCrumbs } = useBreadcrumbContext();

  let crumbs: Crumb[];
  if (pageCrumbs && pageCrumbs.length > 0) {
    crumbs = pageCrumbs;
  } else {
    crumbs = buildBreadcrumbs(location.pathname);
    const eventName =
      isEventScopedRoute(location.pathname) && selectedEvent?.name ? selectedEvent.name : null;
    if (eventName) {
      crumbs.push({ label: eventName });
    }
  }

  if (crumbs.length === 0) return null;

  return (
    <>
      <Separator orientation="vertical" className="hidden h-4 shrink-0 sm:block" />
      <nav aria-label="Breadcrumb" className="hidden min-w-0 items-center sm:flex">
        <ol className="flex min-w-0 items-center gap-1.5 text-sm">
          {crumbs.map((crumb, index) => {
            const isLast = index === crumbs.length - 1;
            return (
              <li key={`${crumb.label}-${index}`} className="flex min-w-0 items-center gap-1.5">
                {index > 0 && (
                  <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                )}
                {crumb.href && !isLast ? (
                  <Link
                    to={crumb.href}
                    className="truncate text-muted-foreground hover:text-foreground hover:underline"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span
                    // The page title (immediately left) now carries the emphasis,
                    // so the trailing crumb no longer competes with it.
                    className={
                      isLast ? 'truncate text-foreground' : 'truncate text-muted-foreground'
                    }
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {crumb.label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
}

/**
 * Sidebar brand mark. Deliberately reuses the app favicon (`public/favicon.ico`)
 * rather than a second copy of the artwork, so the browser tab and the sidebar
 * can never drift apart. Decorative — the adjacent "Quest" wordmark carries the
 * accessible name, so the image is hidden from assistive tech.
 */
function AppLogo(): ReactNode {
  return <img src="/favicon.ico" alt="" aria-hidden className="size-8 object-contain" />;
}

export function RootLayout(): ReactNode {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navGroups = filterNavGroups(NAV_GROUPS, Boolean(angularBaseUrl));

  const groups: SidebarNavCategoryGroup[] = [
    ...navGroups,
    ...(authMyAccountUri
      ? [
          {
            label: 'Account',
            sections: [
              {
                key: 'my-account',
                label: 'My account',
                icon: UserCog,
                external: true,
                externalUrl: authMyAccountUri,
              },
            ],
          },
        ]
      : []),
  ];

  const handleLogout = (): void => {
    // FG front-end logout (VITE_AUTH_LOGOUT_URI) terminates the IdP session
    // directly; otherwise fall back to the standard OIDC end-session flow.
    if (authLogoutUri) {
      window.location.assign(authLogoutUri);
    } else {
      void signOut();
    }
  };

  return (
    <EventProvider>
      <BreadcrumbProvider>
        <PageTitleProvider>
          <InsetSidebarProvider persistenceKey="iplan-web.sidebar">
            <InsetSidebarLayout>
              <InsetSidebar
                sections={[]}
                categoryGroups={groups}
                // Grey sidebar panel against the white main surface. `bg-background`
                // is the theme's light grey (`--background` ≈ oklch 0.968); the
                // component defaults to white `bg-card`, and tailwind-merge lets this
                // last background class win.
                className="bg-background"
                // Canonicalised so legacy /events/:eventId/orders deep links
                // still highlight the "Orders" nav item.
                currentPath={canonicalNavPath(location.pathname)}
                header={{ logo: <AppLogo />, title: 'Quest' }}
                user={
                  user
                    ? {
                        displayName: user.displayName,
                        subtitle: user.email,
                        avatarInitial: user.displayName?.charAt(0)?.toUpperCase(),
                      }
                    : undefined
                }
                onNavigate={(href) => navigate(href)}
                onLogout={handleLogout}
              />
              <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-card">
                {/* Top bar — collapse trigger │ page title │ breadcrumb (left);
                    event selector + search + theme (right). */}
                <header className="flex items-center gap-2 border-b border-border px-4 py-2.5">
                  <InsetSidebarTrigger />
                  <Separator orientation="vertical" className="h-4 shrink-0" />
                  <PageTitle />
                  <Breadcrumbs />
                  <div className="flex-1" />
                  <EventSelector />
                  <GlobalSearch />
                  <ThemeToggle />
                </header>
                {/* py-6 rather than py-8: the page title block that used to sit
                    here has moved into the topbar. */}
                <main className="flex-1 overflow-auto px-8 py-6">
                  <Outlet />
                </main>
              </div>
            </InsetSidebarLayout>
          </InsetSidebarProvider>
        </PageTitleProvider>
      </BreadcrumbProvider>
    </EventProvider>
  );
}

/**
 * Standardised page header. The prop signature is unchanged, but the title no
 * longer renders here: it is published to the topbar (see {@link PageTitle}),
 * which reclaims the title block above every grid. `subtitle` is accepted for
 * source compatibility with the pages that still pass it, and ignored — there
 * is nowhere sensible to put it now.
 *
 * What is left is a slim, right-aligned actions row; a page that passes no
 * `actions` renders nothing at all (no empty box, no margin), so its content
 * starts at the top of the main area.
 */
export function PageHeader({
  title,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}): ReactNode {
  usePageTitle(title);

  if (!actions) return null;
  return (
    <div className="mb-3 flex items-center justify-end gap-2" data-testid="page-actions">
      {actions}
    </div>
  );
}
